import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store';
import { useAuth } from '@/hooks/useAuth';
import { SubscriptionModal } from '@/components/SubscriptionModal';
import { fetchBilling } from '@/store/slices/billingSlice';

/**
 * Global nudge for Limited (recruitee, unpaid) users on a watch page. Shows the
 * daily-allowance / ad modal once per title. Limited users are never hard
 * blocked — they can always continue (a pre-roll ad plays once their daily free
 * plays are spent; the ad itself is handled by the player's AdGate). Mounted
 * once in App; refreshes billing when a new watch route is entered.
 */
export function BillingGate(): JSX.Element | null {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, status } = useAuth();
  const billing = useAppSelector((s) => s.billing.data);

  const isRealUser = status === 'authenticated' && user != null && !user.guest;
  const onWatchRoute = location.pathname.startsWith('/watch/');

  // Per-title dismissal so the modal shows once per watch page.
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);

  // Refresh billing on login and whenever a new watch route is entered.
  useEffect(() => {
    if (isRealUser) void dispatch(fetchBilling());
  }, [isRealUser, dispatch, onWatchRoute ? location.pathname : '']);

  if (!isRealUser || !onWatchRoute || !billing || billing.kind !== 'limited' || !billing.dailyMedia) {
    return null;
  }
  // Only nudge once paywall enforcement is actually on.
  if (!billing.paywallEnabled) return null;
  if (dismissedKey === location.pathname) return null;

  const { remaining, limit, adRequired } = billing.dailyMedia;

  return (
    <SubscriptionModal
      remaining={remaining}
      limit={limit}
      adRequired={adRequired}
      onContinue={() => setDismissedKey(location.pathname)}
      onSubscribe={() => navigate('/subscribe')}
      onBack={() => navigate(-1)}
    />
  );
}
